// API docs — reference page for M2M callers.
//
// Mirrors the endpoints that BearerDispatch (api_key_auth.go) admits for
// `stk_…` keys: register beneficiary, initiate a single payout (two
// variants: pre-registered beneficiary_id OR inline beneficiary fields),
// bulk initiate, and refresh status. No live data — purely static; safe
// to render without merchantId context. Owner+ teammates land here from
// /keys via "View API docs".

import { useState } from "react";

import { I } from "@/components/Icons";
import { useUI } from "@/store/ui";

// Derived from the build-time VITE_PAYOUTS_API_URL — the same value the
// apiClient uses for live requests, so the curl examples line up with
// the host the bundle is actually talking to. Staging build serves
// https://pay.staging.khazana.cards; production build serves
// https://pay.prod.khazana.cards. Falls back to the prod host when the
// variable is unset (e.g. a local `vite` dev run with no .env override)
// so the docs are never empty.
const API_BASE: string =
  (import.meta.env.VITE_PAYOUTS_API_URL as string | undefined) ||
  "https://pay.prod.khazana.cards";

interface Endpoint {
  method: "POST" | "GET";
  path: string;
  scope:
    | "payouts:write"
    | "payouts:read"
    | "balances:read"
    | "accounts:read";
  summary: string;
  body?: string;
  curl: string;
  /**
   * Example response body for read endpoints. Optional because the write
   * surfaces (beneficiaries / payouts / bulk / refresh) all return rich
   * shapes whose authoritative documentation lives elsewhere — the curl
   * block already shows the request, and integrators won't paste the
   * response back into anything.
   */
  response?: string;
}

function endpoints(merchantId: number | string): Endpoint[] {
  return [
    {
      method: "GET",
      path: `/v1/merchants/${merchantId}`,
      scope: "accounts:read",
      summary:
        "Bootstrap call. Returns your merchant profile (id, slug, display_name, status) plus an embedded bank_accounts array — the same shape as the dedicated rails endpoint below. If you only need the ids, use /bank-rails. Use this on first integration to discover your bank_account_id values without an out-of-band lookup.",
      curl: `curl ${API_BASE}/v1/merchants/${merchantId} \\
  -H "Authorization: Bearer stk_…"`,
      response: `{
  "id": 42,
  "slug": "acme",
  "display_name": "Acme Corp",
  "status": "active",
  "created_at": "2026-01-15T09:22:14.512Z",
  "updated_at": "2026-06-02T17:08:01.880Z",
  "bank_accounts": [
    {
      "id": 1,
      "merchant_id": 42,
      "label": "Primary HDFC",
      "provider": "idfc",
      "currency": "INR",
      "status": "active",
      "disabled_at": null,
      "created_at": "2026-01-15T09:23:01.100Z"
    },
    {
      "id": 2,
      "merchant_id": 42,
      "label": "Buckzy Global",
      "provider": "buckzy",
      "currency": "USD",
      "status": "active",
      "disabled_at": null,
      "created_at": "2026-03-08T14:05:42.020Z"
    }
  ]
}`,
    },
    {
      method: "GET",
      path: `/v1/merchants/${merchantId}/bank-accounts`,
      scope: "accounts:read",
      summary:
        "List the bank rails attached to your merchant. Returns id, label, provider, currency, and status (\"active\", \"pending\" or \"disabled\"). No account_no / ifsc / credentials are ever returned. Use the id values as bank_account_id when initiating a payout.",
      curl: `curl ${API_BASE}/v1/merchants/${merchantId}/bank-accounts \\
  -H "Authorization: Bearer stk_…"`,
      response: `{
  "bank_accounts": [
    {
      "id": 1,
      "merchant_id": 42,
      "label": "Primary HDFC",
      "provider": "idfc",
      "currency": "INR",
      "status": "active",
      "disabled_at": null,
      "created_at": "2026-01-15T09:23:01.100Z"
    },
    {
      "id": 2,
      "merchant_id": 42,
      "label": "Buckzy Global",
      "provider": "buckzy",
      "currency": "USD",
      "status": "active",
      "disabled_at": null,
      "created_at": "2026-03-08T14:05:42.020Z"
    }
  ]
}`,
    },
    {
      method: "GET",
      path: `/v1/merchants/${merchantId}/bank-rails`,
      scope: "accounts:read",
      summary:
        "Just the ids of your bank rails, each with its status (\"active\", \"pending\" or \"disabled\"). No label or provider. Only \"active\" rails accept payouts; pass the id as bank_account_id.",
      curl: `curl ${API_BASE}/v1/merchants/${merchantId}/bank-rails \\
  -H "Authorization: Bearer stk_…"`,
      response: `{
  "bank_rails": [
    { "id": 1, "status": "active" },
    { "id": 2, "status": "active" }
  ]
}`,
    },
    {
      method: "POST",
      path: `/v1/merchants/${merchantId}/beneficiaries`,
      scope: "payouts:write",
      summary:
        "Register a payee explicitly. Optional — the single-payout and bulk endpoints can also create a beneficiary inline. Use this when you want to verify a payee before sending money, or to reuse a pre-registered beneficiary_id across many calls.",
      body: `{
  "bank_account_id": 1,
  "name": "Acme Vendors Pvt Ltd",
  "account_no": "1234567890123",
  "ifsc": "HDFC0001234",
  "email": "ap@acme.example",
  "phone": "9123456789"
}`,
      curl: `curl -X POST ${API_BASE}/v1/merchants/${merchantId}/beneficiaries \\
  -H "Authorization: Bearer stk_…" \\
  -H "Content-Type: application/json" \\
  -d '{
    "bank_account_id": 1,
    "name": "Acme Vendors Pvt Ltd",
    "account_no": "1234567890123",
    "ifsc": "HDFC0001234",
    "email": "ap@acme.example",
    "phone": "9123456789"
  }'`,
    },
    {
      method: "POST",
      path: `/v1/merchants/${merchantId}/payouts`,
      scope: "payouts:write",
      summary:
        "Initiate a single payout. Provide either a previously-registered beneficiary_id (201, synchronous — the row enters the approval queue immediately), or an inline beneficiary object (202, asynchronous — the partner bank registers the payee, then the row enters the approval queue). amount_minor is paise (100000 = ₹1,000.00). txn_mode: 1=IMPS, 2=NEFT. request_id is your idempotency key — replaying the same value returns the original payout.",
      body: `// Option A — pre-registered payee
{
  "bank_account_id": 1,
  "beneficiary_id": 42,
  "amount_minor": 100000,
  "txn_mode": 1,
  "request_id": "order-2026-06-03-0001"
}

// Option B — inline beneficiary (register + pay in one call)
{
  "bank_account_id": 1,
  "beneficiary": {
    "name": "Acme Vendors Pvt Ltd",
    "account_no": "1234567890123",
    "ifsc": "HDFC0001234",
    "email": "ap@acme.example",
    "phone": "9123456789"
  },
  "amount_minor": 100000,
  "txn_mode": 1,
  "request_id": "order-2026-06-03-0001"
}`,
      curl: `curl -X POST ${API_BASE}/v1/merchants/${merchantId}/payouts \\
  -H "Authorization: Bearer stk_…" \\
  -H "Content-Type: application/json" \\
  -d '{
    "bank_account_id": 1,
    "beneficiary": {
      "name": "Acme Vendors Pvt Ltd",
      "account_no": "1234567890123",
      "ifsc": "HDFC0001234",
      "email": "ap@acme.example",
      "phone": "9123456789"
    },
    "amount_minor": 100000,
    "txn_mode": 1,
    "request_id": "order-2026-06-03-0001"
  }'`,
    },
    {
      method: "POST",
      path: `/v1/merchants/${merchantId}/payouts/bulk`,
      scope: "payouts:write",
      summary:
        "Initiate up to 100 payouts in one request. Each row carries its own request_id (idempotency) and either beneficiary_id or inline beneficiary fields. Returns 202 with a batch_id you can use to poll group status, plus per-row results so you can correlate by request_id without depending on row order. Rows with inline beneficiaries land in pending_bene_registration and flip to needs_approval as the background worker registers each payee.",
      body: `{
  "bank_account_id": 1,
  "rows": [
    {
      "beneficiary_id": 42,
      "amount_minor": 100000,
      "txn_mode": 1,
      "request_id": "ord-001"
    },
    {
      "beneficiary": {
        "name": "Beta Suppliers",
        "account_no": "9876543210987",
        "ifsc": "ICIC0009876",
        "email": "ap@beta.example",
        "phone": "9988776655"
      },
      "amount_minor": 250000,
      "txn_mode": 2,
      "request_id": "ord-002"
    }
  ]
}`,
      curl: `curl -X POST ${API_BASE}/v1/merchants/${merchantId}/payouts/bulk \\
  -H "Authorization: Bearer stk_…" \\
  -H "Content-Type: application/json" \\
  -d '{
    "bank_account_id": 1,
    "rows": [
      {
        "beneficiary_id": 42,
        "amount_minor": 100000,
        "txn_mode": 1,
        "request_id": "ord-001"
      },
      {
        "beneficiary": {
          "name": "Beta Suppliers",
          "account_no": "9876543210987",
          "ifsc": "ICIC0009876",
          "email": "ap@beta.example",
          "phone": "9988776655"
        },
        "amount_minor": 250000,
        "txn_mode": 2,
        "request_id": "ord-002"
      }
    ]
  }'`,
    },
    {
      method: "POST",
      path: `/v1/merchants/${merchantId}/payouts/:pid/refresh`,
      scope: "payouts:read",
      summary:
        "Force a status poll for a pending payout. The reconciler also polls on a schedule — only use this endpoint when you need a synchronous update (e.g. after a callback you wanted to verify).",
      curl: `curl -X POST ${API_BASE}/v1/merchants/${merchantId}/payouts/123/refresh \\
  -H "Authorization: Bearer stk_…"`,
    },
  ];
}

function CodeBlock({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);
  function copy() {
    navigator.clipboard?.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
  }
  return (
    <div
      style={{
        position: "relative",
        background: "var(--bg-sunk)",
        border: "1px solid var(--hair)",
        borderRadius: 3,
      }}
    >
      <button
        className="addon"
        type="button"
        onClick={copy}
        style={{
          position: "absolute",
          top: 8,
          right: 8,
          padding: "4px 10px",
          cursor: "pointer",
          background: "var(--bg)",
          border: "1px solid var(--hair)",
          borderRadius: 2,
          fontSize: 11,
          color: "var(--ink)",
          display: "flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        {copied ? (
          <>
            <I.Check size={11} stroke={2.5} /> Copied
          </>
        ) : (
          <>
            <I.Copy size={11} /> Copy
          </>
        )}
      </button>
      <pre
        style={{
          margin: 0,
          padding: "14px 56px 14px 14px",
          fontFamily: "var(--font-mono)",
          fontSize: 11.5,
          color: "var(--ink-2)",
          lineHeight: 1.6,
          overflowX: "auto",
          whiteSpace: "pre",
        }}
      >
        {code}
      </pre>
    </div>
  );
}

export function ApiDocs() {
  // Prefer the slug for curl examples — readable, brand-aligned, and what
  // the operator's M2M integration should actually use. Fall back to the
  // int id, then a placeholder "acme" slug when no merchant is in context
  // (admin without selection). Backend resolves either form so any of
  // these copy-pastes work as soon as the operator substitutes their own
  // merchant.
  const slug = useUI((s) => s.currentMerchantSlug);
  const id = useUI((s) => s.currentMerchantId);
  const identifier: string | number = slug ?? id ?? "acme";
  const eps = endpoints(identifier);

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <div className="eyebrow">Console · Developer</div>
          <h1>API reference</h1>
          <div className="sub">
            Six endpoints accept your <code>stk_…</code> key as a bearer
            token. All requests go to <code>{API_BASE}</code>.
          </div>
        </div>
      </div>

      {/* Auth + approval notes */}
      <div className="stack-16" style={{ marginBottom: 16 }}>
        <div
          style={{
            background: "var(--bg-sunk)",
            padding: "10px 14px",
            borderRadius: 3,
            borderLeft: "2px solid var(--accent)",
            display: "flex",
            gap: 10,
            alignItems: "flex-start",
          }}
        >
          <I.Info size={14} style={{ marginTop: 2, color: "var(--accent)" }} />
          <div style={{ fontSize: 12 }}>
            <div style={{ fontWeight: 500, marginBottom: 4 }}>
              Authentication
            </div>
            <div className="dim">
              Send your secret in the <code>Authorization</code> header as{" "}
              <code>Bearer stk_…</code>. Merchant scope is bound to the key —
              no <code>X-Merchant-ID</code> header needed. The{" "}
              <code>:id</code> in the URL must still match your merchant id
              for the route to parse.
            </div>
          </div>
        </div>
        <div
          style={{
            background: "var(--warn-soft)",
            padding: "10px 14px",
            borderRadius: 3,
            borderLeft: "2px solid var(--warn)",
            display: "flex",
            gap: 10,
            alignItems: "flex-start",
          }}
        >
          <I.AlertCircle
            size={14}
            style={{ marginTop: 2, color: "var(--warn)" }}
          />
          <div style={{ fontSize: 12 }}>
            <div style={{ fontWeight: 500, marginBottom: 4 }}>
              Approval required
            </div>
            <div className="dim">
              Every payout you submit (single or bulk) enters as{" "}
              <code>needs_approval</code> and is dispatched only after an
              admin clicks Approve in the console. No funds move until then.
            </div>
          </div>
        </div>
      </div>

      {/* Endpoint cards */}
      <div className="stack-16">
        {eps.map((ep) => (
          <div className="card" key={ep.path}>
            <div
              style={{
                padding: "14px 18px",
                borderBottom: "1px solid var(--hair)",
                display: "flex",
                alignItems: "center",
                gap: 10,
                flexWrap: "wrap",
              }}
            >
              <span
                className="mono"
                style={{
                  fontSize: 11,
                  padding: "2px 8px",
                  background: "var(--tint)",
                  border: "1px solid var(--hair)",
                  borderRadius: 2,
                  fontWeight: 600,
                  color: "var(--ink)",
                }}
              >
                {ep.method}
              </span>
              <span
                className="mono"
                style={{ fontSize: 12.5, color: "var(--ink)" }}
              >
                {ep.path}
              </span>
              <div className="flex-1" />
              <span
                className="mono"
                style={{
                  fontSize: 11,
                  padding: "2px 8px",
                  border: "1px solid var(--hair)",
                  borderRadius: 2,
                  color: "var(--ink-2)",
                }}
                title="Required scope on the API key"
              >
                {ep.scope}
              </span>
            </div>
            <div style={{ padding: "14px 18px" }}>
              <p
                className="dim"
                style={{ fontSize: 12.5, margin: "0 0 12px 0" }}
              >
                {ep.summary}
              </p>
              {ep.body && (
                <div className="field" style={{ marginBottom: 12 }}>
                  <label className="label">Request body</label>
                  <CodeBlock code={ep.body} />
                </div>
              )}
              <div
                className="field"
                style={{ marginBottom: ep.response ? 12 : 0 }}
              >
                <label className="label">Example</label>
                <CodeBlock code={ep.curl} />
              </div>
              {ep.response && (
                <div className="field" style={{ marginBottom: 0 }}>
                  <label className="label">Example response</label>
                  <CodeBlock code={ep.response} />
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Webhooks */}
      <div className="card" style={{ marginTop: 16 }}>
        <div
          style={{
            padding: "14px 18px",
            borderBottom: "1px solid var(--hair)",
            display: "flex",
            alignItems: "center",
            gap: 10,
            flexWrap: "wrap",
          }}
        >
          <div className="eyebrow">Webhooks</div>
          <div className="flex-1" />
          <span
            className="dim"
            style={{ fontSize: 11 }}
          >
            Manage endpoints under <code>Console → Webhooks</code> (owner-only).
          </span>
        </div>
        <div style={{ padding: "14px 18px" }} className="stack-16">
          <p className="dim" style={{ fontSize: 12.5, margin: 0 }}>
            Register an HTTPS endpoint to receive push notifications when a
            payout changes status. Each delivery is POSTed as JSON with an
            HMAC-SHA256 signature your server should verify before
            trusting the payload. Retries follow exponential backoff up
            to 5 attempts (~13 minutes total) before dead-letter; the
            Webhooks page in the console shows every attempt and its
            outcome.
          </p>

          <div className="field" style={{ marginBottom: 0 }}>
            <label className="label">Event types</label>
            <dl className="dl tight">
              <dt><code>payout.approved</code></dt>
              <dd className="dim">Admin clicked Approve. Row transitioned <code>needs_approval → pending</code>; the bank dispatch is in flight.</dd>
              <dt><code>payout.success</code></dt>
              <dd className="dim">Settlement confirmed. The payload's <code>utr_no</code> is populated.</dd>
              <dt><code>payout.failure</code></dt>
              <dd className="dim">Bank rejected the payout or the reconciler gave up. <code>error_code</code> + <code>error_message</code> carry the reason.</dd>
            </dl>
          </div>

          <div className="field" style={{ marginBottom: 0 }}>
            <label className="label">Request headers</label>
            <CodeBlock
              code={`POST /your/webhook/path
Content-Type: application/json
User-Agent: payouts-webhook/1
X-Webhook-Event: payout.success
X-Webhook-Event-Id: evt_20260605T193015000_a3b9c2
X-Webhook-Signature: t=1717000000,v1=<hex-hmac-sha256>`}
            />
          </div>

          <div className="field" style={{ marginBottom: 0 }}>
            <label className="label">Payload</label>
            <CodeBlock
              code={`{
  "id": "evt_20260605T193015000_a3b9c2",
  "event": "payout.success",
  "created_at": "2026-06-05T19:30:15.123Z",
  "payout": {
    "id": 123,
    "merchant_id": 42,
    "batch_id": null,
    "request_id": "order-2026-06-05-0001",
    "status": "success",
    "amount_minor": 100000,
    "currency": "INR",
    "txn_mode": 1,
    "provider_txn_id": "…",
    "utr_no": "012345678901",
    "error_code": null,
    "error_message": null,
    "created_at": "…",
    "updated_at": "…"
  }
}`}
            />
          </div>

          <div className="field" style={{ marginBottom: 0 }}>
            <label className="label">Verify the signature</label>
            <p className="dim" style={{ fontSize: 12, marginTop: 0, marginBottom: 8 }}>
              Compute <code>HMAC-SHA256(secret, "{`<t>.<raw_body>`}")</code>{" "}
              and compare hex-encoded against the <code>v1=</code> segment
              of <code>X-Webhook-Signature</code>. Reject deliveries where{" "}
              <code>t</code> is more than 5 minutes off your clock to
              prevent replay.
            </p>
            <CodeBlock
              code={`# Node.js example
const crypto = require("crypto");

function verify(rawBody, header, secret) {
  const parts = Object.fromEntries(
    header.split(",").map((kv) => kv.split("=")),
  );
  const t = parts.t, sig = parts.v1;
  // Reject stale deliveries (replay guard)
  if (Math.abs(Date.now() / 1000 - Number(t)) > 300) return false;
  const expected = crypto
    .createHmac("sha256", Buffer.from(secret, "hex"))
    .update(t + "." + rawBody)
    .digest("hex");
  return crypto.timingSafeEqual(
    Buffer.from(sig, "hex"),
    Buffer.from(expected, "hex"),
  );
}`}
            />
          </div>

          <div className="field" style={{ marginBottom: 0 }}>
            <label className="label">Retry schedule</label>
            <p className="dim" style={{ fontSize: 12, margin: 0 }}>
              Attempts fire at <strong>0s, 5s, 25s, ~2m, ~10m</strong>{" "}
              (with ±20% jitter). Any 2xx response stops retries. After
              the 5th failed attempt the delivery is dead-lettered and
              surfaces in the Webhooks console with <code>status=failed</code>.
              Always respond <strong>within 15 seconds</strong> — slower
              responses count as transport failures.
            </p>
          </div>

          <div className="field" style={{ marginBottom: 0 }}>
            <label className="label">Idempotency</label>
            <p className="dim" style={{ fontSize: 12, margin: 0 }}>
              The <code>X-Webhook-Event-Id</code> header is stable across
              retries for a given delivery — dedupe on it. Your endpoint
              must be idempotent: at-least-once delivery is the contract.
            </p>
          </div>
        </div>
      </div>

      {/* Error codes */}
      <div className="card" style={{ marginTop: 16 }}>
        <div
          style={{
            padding: "14px 18px",
            borderBottom: "1px solid var(--hair)",
          }}
        >
          <div className="eyebrow">Errors</div>
        </div>
        <div style={{ padding: "14px 18px" }}>
          <dl className="dl tight">
            <dt>
              <code>401 missing_bearer</code>
            </dt>
            <dd className="dim">
              No <code>Authorization</code> header or empty value.
            </dd>
            <dt>
              <code>401 api_key_unknown</code>
            </dt>
            <dd className="dim">
              Prefix not found. Check the key wasn't truncated when copied.
            </dd>
            <dt>
              <code>401 api_key_invalid</code>
            </dt>
            <dd className="dim">
              Prefix matched but the secret hash differs.
            </dd>
            <dt>
              <code>401 api_key_inactive</code>
            </dt>
            <dd className="dim">Key was rotated or revoked. Mint a fresh one.</dd>
            <dt>
              <code>403 scope_insufficient</code>
            </dt>
            <dd className="dim">
              Key doesn't carry the scope this route requires.
            </dd>
            <dt>
              <code>400 beneficiary_required</code>
            </dt>
            <dd className="dim">
              Single payout body had neither <code>beneficiary_id</code> nor{" "}
              <code>beneficiary</code>, or it had both.
            </dd>
            <dt>
              <code>422 invalid_row_resolution</code>
            </dt>
            <dd className="dim">
              Bulk row had neither <code>beneficiary_id</code> nor{" "}
              <code>beneficiary</code>, or it had both. Response carries the
              offending <code>row_index</code>.
            </dd>
            <dt>
              <code>422 batch_too_large</code>
            </dt>
            <dd className="dim">
              Bulk request exceeded 100 rows. Chunk client-side.
            </dd>
            <dt>
              <code>422 duplicate_request_id_in_batch</code>
            </dt>
            <dd className="dim">
              Two bulk rows share the same <code>request_id</code>. Response
              carries the two <code>row_indices</code>.
            </dd>
            <dt>
              <code>409 request_id_conflict</code>
            </dt>
            <dd className="dim">
              A previous payout exists for this merchant with the same{" "}
              <code>request_id</code> but different payload. Pick a fresh id.
            </dd>
          </dl>
        </div>
      </div>
    </div>
  );
}
